<template>
    <SliderLarge :open="open" :formClose="close" title="Novo Contato" maxWidth="32rem">
        <div class="flex flex-col h-full">
            <!-- Content -->
            <div class="flex-1 overflow-y-auto p-4">
                <div class="space-y-4">
                    <!-- Avatar Upload -->
                    <div class="flex justify-center mb-6">
                        <div class="relative">
                            <div v-if="formData.avatar" class="w-20 h-20 rounded-full overflow-hidden border-2 border-gray-200">
                                <img :src="formData.avatar" alt="Avatar" class="w-full h-full object-cover" />
                            </div>
                            <div v-else class="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 border-2 border-gray-300">
                                <UserIcon class="w-10 h-10" />
                            </div>
                            <label
                                class="absolute bottom-0 right-0 bg-white border border-gray-300 rounded-full p-1.5 shadow-sm hover:bg-gray-50 cursor-pointer"
                                title="Adicionar foto"
                            >
                                <CameraIcon class="w-4 h-4 text-gray-500" />
                                <input
                                    type="file"
                                    accept="image/*"
                                    @change="handleAvatarUpload"
                                    class="hidden"
                                />
                            </label>
                        </div>
                    </div>

                    <!-- Seletor de Concierge (opcional - quando showAgentSelector é true) -->
                    <div v-if="showAgentSelector">
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            <span class="text-rose-600">*</span> Concierge
                        </label>
                        <select
                            v-model="formData.agents_id"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 text-sm"
                        >
                            <option value="">Selecione um concierge...</option>
                            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
                                {{ agent.name }}
                            </option>
                        </select>
                    </div>

                    <!-- Nome -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                            <span class="text-rose-600">*</span> Nome
                        </label>
                        <input
                            type="text"
                            v-model="formData.name"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 text-sm"
                            placeholder="Nome do contato"
                        />
                    </div>

                    <!-- Email -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input
                            type="email"
                            v-model="formData.email"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 text-sm"
                            placeholder="email@exemplo.com"
                        />
                    </div>

                    <!-- Telefone -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                        <input
                            type="text"
                            v-model="formData.phone"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 text-sm"
                            placeholder="(00) 00000-0000"
                        />
                    </div>

                    <!-- Descricao -->
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Descricao</label>
                        <textarea
                            v-model="formData.description"
                            rows="3"
                            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-400 focus:border-orange-400 text-sm"
                            placeholder="Observacoes sobre o contato..."
                        ></textarea>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="flex-shrink-0 px-4 py-4 flex gap-4 justify-between items-center border-t">
                <button @click="close" type="button" class="secondaryButton">
                    Cancelar
                </button>
                <button
                    @click="save"
                    type="button"
                    class="primaryButton"
                    :disabled="saving || !isFormValid"
                >
                    {{ saving ? 'Salvando...' : 'Criar Contato' }}
                </button>
            </div>
        </div>
    </SliderLarge>
</template>

<script>
import { CameraIcon, UserIcon } from '@heroicons/vue/24/outline';

export default {
    name: 'CreateContactSlider',
    components: {
        CameraIcon,
        UserIcon,
    },
    props: {
        open: {
            type: Boolean,
            default: false
        },
        agentId: {
            type: String,
            default: null
        },
        showAgentSelector: {
            type: Boolean,
            default: false
        },
        initialName: {
            type: String,
            default: ''
        }
    },
    emits: ['close', 'created'],
    data() {
        return {
            saving: false,
            agents: [],
            formData: {
                name: '',
                email: '',
                phone: '',
                description: '',
                avatar: '',
                agents_id: '',
                origin: 'manual'
            },
        };
    },
    computed: {
        isFormValid() {
            const hasName = this.formData.name && this.formData.name.trim().length >= 2;
            const hasAgent = !this.showAgentSelector || this.formData.agents_id;
            return hasName && hasAgent;
        }
    },
    watch: {
        open: {
            immediate: true,
            handler(isOpen) {
                if (isOpen) {
                    this.resetForm();
                    if (this.showAgentSelector) {
                        this.loadAgents();
                    }
                }
            }
        },
        agentId: {
            immediate: true,
            handler(newVal) {
                if (newVal && !this.showAgentSelector) {
                    this.formData.agents_id = newVal;
                }
            }
        }
    },
    methods: {
        resetForm() {
            this.formData = {
                name: this.initialName || '',
                email: '',
                phone: '',
                description: '',
                avatar: '',
                agents_id: this.agentId || '',
                origin: 'manual'
            };
        },

        async loadAgents() {
            try {
                const response = await this.$ApiService.get('agents', { limit: 50 });
                if (response.data?.rows) {
                    this.agents = response.data.rows;
                }
            } catch (error) {
                console.error('Erro ao carregar agentes:', error);
            }
        },

        handleAvatarUpload(event) {
            const file = event.target.files[0];
            if (!file) return;

            // Validar tipo de arquivo
            if (!file.type.startsWith('image/')) {
                alert('Por favor, selecione uma imagem.');
                return;
            }

            // Validar tamanho (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                alert('A imagem deve ter no maximo 5MB.');
                return;
            }

            // Converter para base64 para preview (upload sera feito no save)
            const reader = new FileReader();
            reader.onload = (e) => {
                this.formData.avatar = e.target.result;
            };
            reader.readAsDataURL(file);
        },

        async uploadAvatarToServer(base64Avatar) {
            try {
                const response = await this.$ApiService.post('crm/contacts/upload/avatar', {
                    avatar: base64Avatar
                });
                if (response.data?.url) {
                    return response.data.url;
                }
                return null;
            } catch (error) {
                console.error('Erro ao fazer upload do avatar:', error);
                return null;
            }
        },

        async save() {
            if (!this.isFormValid) return;

            this.saving = true;
            try {
                let avatarUrl = null;

                // Se tem avatar base64, faz upload primeiro
                if (this.formData.avatar && this.formData.avatar.startsWith('data:image')) {
                    avatarUrl = await this.uploadAvatarToServer(this.formData.avatar);
                } else if (this.formData.avatar) {
                    avatarUrl = this.formData.avatar;
                }

                const payload = {
                    name: this.formData.name.trim(),
                    email: this.formData.email?.trim() || null,
                    phone: this.formData.phone?.trim() || null,
                    description: this.formData.description?.trim() || null,
                    avatar: avatarUrl,
                    origin: 'manual',
                };

                // Adiciona agents_id se fornecido
                if (this.formData.agents_id) {
                    payload.agents_id = this.formData.agents_id;
                }

                const response = await this.$ApiService.post('crm/contacts', payload);

                if (response.data?.id) {
                    this.$emit('created', response.data);
                    this.close();
                } else if (response.error) {
                    alert('Erro ao criar contato: ' + (response.error.message || response.error));
                }
            } catch (error) {
                console.error('Erro ao criar contato:', error);
                alert('Erro ao criar contato');
            } finally {
                this.saving = false;
            }
        },

        close() {
            this.resetForm();
            this.$emit('close');
        }
    }
};
</script>
